import React, { useMemo, useState, useEffect } from "react";
import {
  BaseModal,
  BaseModalBody,
  BaseModalHeader,
  BaseModalFooter,
} from "@src/common/components";
import CourieButton from "@src/common/components/Button";
import {
  useGetShipmentDistancesQuery,
  useClearOrderLegDistanceOverrideMutation,
  Stop,
  Shipment,
} from "@api/graphql/generated/generated-types";
import { useOverrideOrderMiles } from "@src/orders/hooks/useOverrideOrderMiles";
import { Loading } from "@src/common/components/Loading";
import { Input } from "@src/common/components";
import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { useAuth } from "@src/common/hooks/useAuth";
import { useOrderIdentifierColumnType } from "@src/shipments/hooks/useOrderIdentifierColumnType";
import { resolveOrderIdentifier } from "@src/shipments/utils/orderIdentifierColumn";
import {
  TripDistancesTable,
  TripDistanceData,
} from "@src/common/components/TripDistancesTable";
import { useCourierLocale } from "@src/common/courierLocale";
import { metersToMiles } from "@src/common/lib/NumberUtils";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { useCourieStore } from "@src/common/lib/store";
import WarningConfirmationModal from "@src/common/components/Modal/WarningConfirmationModal";
import { GlobalMileageOverrideModal } from "./GlobalMileageOverrideModal";
import { ApolloError } from "@apollo/client";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  shipment: Shipment;
  isWholeOrderPricing?: boolean;
  onMilesOverrideSaved?: () => void;
};

// Rounds to 1 decimal for display, except that a genuinely nonzero distance
// is never rounded down to "0" — GlobalMileageOverrideModal treats an
// untouched "0" prefill as an explicit request to zero the leg out, so this
// value must not lie about a leg actually being zero.
function roundDisplayDistanceWithoutFalseZero(miles: number): number {
  const roundedToOneDecimal = Number(miles.toFixed(1));
  if (roundedToOneDecimal !== 0 || miles === 0) return roundedToOneDecimal;
  return Number(miles.toFixed(3));
}

export function ShipmentDistanceModal({
  isOpen,
  onClose,
  shipment,
  isWholeOrderPricing = false,
  onMilesOverrideSaved,
}: Props) {
  const { courierId } = useAuth();
  const orderIdentifierColumnType = useOrderIdentifierColumnType();
  const { showToast } = useCourieStore();
  const showError = useErrorToast();
  const { formatDistance, milesToDisplay } = useCourierLocale();

  const [
    overrideOrderMilesMutation,
    { loading: overrideOrderMilesMutationLoading },
  ] = useOverrideOrderMiles();
  const [
    clearOrderLegDistanceOverride,
    { loading: clearOrderLegDistanceOverrideLoading },
  ] = useClearOrderLegDistanceOverrideMutation();
  const [milesOverrideInput, setMilesOverrideInput] = useState<string>("");
  const [selectedTrip, setSelectedTrip] = useState<TripDistanceData>();
  const [tripPendingClear, setTripPendingClear] = useState<TripDistanceData>();

  const isClosed: boolean = shipment.isClosed ?? false;
  const orderId: string = shipment.order.id;

  useEffect(() => {
    const override = shipment.order.milesOverride;
    setMilesOverrideInput(override != null ? String(override) : "");
  }, [shipment.order.milesOverride]);

  const handleSaveMilesOverride = () => {
    if (!orderId || !courierId) return;
    const trimmed: string = milesOverrideInput.trim();
    const parsedMiles: number | null = trimmed === "" ? null : Number(trimmed);
    if (parsedMiles !== null && isNaN(parsedMiles)) return;
    overrideOrderMilesMutation({
      variables: { courierId, orderId, milesOverride: parsedMiles },
    })
      .then(() => onMilesOverrideSaved?.())
      .catch((e) => showErrorToast(e, showToast));
  };

  const handleClearMilesOverride = () => {
    if (!orderId || !courierId) return;
    overrideOrderMilesMutation({
      variables: { courierId, orderId, milesOverride: null },
    })
      .then(() => {
        setMilesOverrideInput("");
        onMilesOverrideSaved?.();
      })
      .catch((e) => showErrorToast(e, showToast));
  };

  const { data, loading, error, refetch } = useGetShipmentDistancesQuery({
    variables: {
      courierId: courierId || "",
      shipmentIds: [shipment.id],
    },
    skip: !isOpen || !shipment.id || !courierId,
    fetchPolicy: "network-only",
  });

  const distances = data?.shipmentDistances?.[0];

  const formatAddress = (stop: Stop) => {
    const parts = [
      stop.streetAddress,
      stop.city,
      stop.state,
      stop.zipCode,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const trips: TripDistanceData[] = useMemo(() => {
    if (!distances?.stopTripDistances) return [];

    return distances.stopTripDistances.map((tripDistance) => ({
      sourceStopId: tripDistance.sourceStopId,
      destinationStopId: tripDistance.destinationStopId,
      origin: {
        companyName: tripDistance.sourceStop.companyName || undefined,
        address: formatAddress(tripDistance.sourceStop as Stop),
      },
      destination: {
        companyName: tripDistance.destinationStop.companyName || undefined,
        address: formatAddress(tripDistance.destinationStop as Stop),
      },
      formattedDistance: formatDistance(
        tripDistance.tripDistance.distanceInMeters
      ),
      distanceInMeters: tripDistance.tripDistance.distanceInMeters,
      displayDistance: roundDisplayDistanceWithoutFalseZero(
        milesToDisplay(
          metersToMiles(tripDistance.tripDistance.distanceInMeters)
        )
      ),
      isOverridden: tripDistance.tripDistance.isOverridden,
    }));
  }, [distances, formatDistance, milesToDisplay]);

  const handleOverride = (trip: TripDistanceData) => {
    if (!trip.isOverridden) {
      setSelectedTrip(trip);
      return;
    }
    setTripPendingClear(trip);
  };

  const handleConfirmClear = () => {
    if (
      !courierId ||
      !tripPendingClear?.sourceStopId ||
      !tripPendingClear?.destinationStopId
    )
      return;
    clearOrderLegDistanceOverride({
      variables: {
        courierId,
        orderId,
        originStopId: tripPendingClear.sourceStopId,
        destinationStopId: tripPendingClear.destinationStopId,
      },
    })
      .then(() => {
        showToast({
          message: "Global mileage override cleared",
          type: "success",
        });
        setTripPendingClear(undefined);
        onMilesOverrideSaved?.();
        return refetch();
      })
      .catch((err) => showError(err as ApolloError));
  };

  const totalDistanceFormatted = useMemo(() => {
    if (!distances?.distanceMeters) return formatDistance(0);
    return formatDistance(distances.distanceMeters);
  }, [distances, formatDistance]);

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        size="auto"
        overrides={{
          Root: {
            style: {
              zIndex: 9999,
            },
          },
          Dialog: {
            style: {
              width: "900px",
              maxWidth: "90vw",
            },
          },
        }}
      >
        <BaseModalHeader>
          Trip Distances - Order #
          {resolveOrderIdentifier(
            {
              trackingNumber: shipment.trackingNumber,
              displayId: shipment.order.displayId,
            },
            orderIdentifierColumnType,
          )}
        </BaseModalHeader>
        <BaseModalBody>
          {loading ? (
            <div className="flex justify-center p-4">
              <Loading loading={true} />
            </div>
          ) : error ? (
            <div className="text-red-500 p-4">
              Error loading distances: {error.message}
            </div>
          ) : (
            // Unlike the whole-order miles override below, leg overrides stay
            // enabled on closed orders: correcting billed mileage during
            // pre-invoice review — after the shipment completes — is the
            // main reason this feature exists.
            <TripDistancesTable trips={trips} onOverride={handleOverride} />
          )}
          {isWholeOrderPricing && (
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total miles override
                </span>
                {shipment.order.milesOverride != null && !isClosed && (
                  <span
                    onClick={handleClearMilesOverride}
                    className="flex items-center text-xs cursor-pointer text-blue-500 hover:underline"
                  >
                    <ArrowUturnLeftIcon className="h-3 w-3 mr-1" />
                    Clear
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Input
                    value={milesOverrideInput}
                    onChange={(e) =>
                      setMilesOverrideInput(
                        (e.target as HTMLInputElement).value
                      )
                    }
                    placeholder="New miles to destination"
                    endEnhancer="mi"
                    disabled={isClosed}
                  />
                </div>
                {!isClosed && (
                  <CourieButton
                    size="xs"
                    onClick={handleSaveMilesOverride}
                    isProcessing={overrideOrderMilesMutationLoading}
                  >
                    Save
                  </CourieButton>
                )}
              </div>
              {isClosed && (
                <p className="text-xs text-slate-400 mt-1">
                  Order is closed — miles override cannot be changed
                </p>
              )}
            </div>
          )}
        </BaseModalBody>
        <BaseModalFooter>
          <div className="flex justify-between items-center w-full">
            <div className="font-bold text-lg">
              Total Distance: {totalDistanceFormatted}
            </div>
            <CourieButton color="gray" onClick={onClose}>
              Close
            </CourieButton>
          </div>
        </BaseModalFooter>
      </BaseModal>
      <GlobalMileageOverrideModal
        isOpen={selectedTrip != null}
        onClose={() => setSelectedTrip(undefined)}
        orderId={orderId}
        trip={selectedTrip}
        onSaved={() => {
          refetch();
          onMilesOverrideSaved?.();
        }}
      />
      <WarningConfirmationModal
        isModalOpen={tripPendingClear != null}
        setIsModalOpen={(isOpen) => {
          if (!isOpen) setTripPendingClear(undefined);
        }}
        loading={clearOrderLegDistanceOverrideLoading}
        onConfirm={handleConfirmClear}
        onCancel={() => setTripPendingClear(undefined)}
        title="Clear Global Mileage Override"
        textBody="This removes the mileage override for this address pair everywhere it's used, not just on this order. Future trips between these addresses will use the calculated distance again."
        primaryButtonText="Clear Override"
        primaryButtonColor="failure"
        className="z-[10000]"
      />
    </>
  );
}
